import moment from "moment";
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { filterDriverSummaryReportApi } from "../../api/order";
import { IDriverReportRow } from "../../interface/order";
import { setAppLoading } from "../../redux/app.reducer";
import { useAppDispatch } from "../../redux/hooks";
import useSnackbar from "../../utils/snackbar";

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "-";
  return moment(dateStr).format("DD/MM/YYYY");
};

const formatNumber = (
  value: number | null | undefined,
  decimals: number = 2
): string => {
  if (value === null || value === undefined) return "-";
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

const ReportOverview = () => {
  const { from, to } = useOutletContext<{ from: number; to: number }>();
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar({ position: "bottom-left" });
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<IDriverReportRow[]>([]);

  const fetchDriverSummaryReport = async (from: number, to: number) => {
    try {
      setLoading(true);
      const response = await filterDriverSummaryReportApi(from, to);
      setData(response.data ?? []);
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      let msg: string | undefined;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something wrong.");
    }
  };

  useEffect(() => {
    fetchDriverSummaryReport(from, to);
    // eslint-disable-next-line
  }, [from, to]);

  useEffect(() => {
    dispatch(setAppLoading(loading));
    // eslint-disable-next-line
  }, [loading]);

  return (
    <>
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-bordered table-sm">
              <thead>
                <tr>
                  <th rowSpan={2} className="text-center align-middle">
                    Date
                  </th>
                  <th rowSpan={2} className="text-center align-middle">
                    Order No
                  </th>
                  <th rowSpan={2} className="text-center align-middle">
                    Driver name
                  </th>
                  <th rowSpan={2} className="text-center align-middle">
                    Driver ID
                  </th>
                  <th rowSpan={2} className="text-center align-middle">
                    Location Start
                  </th>
                  <th rowSpan={2} className="text-center align-middle">
                    Location Ending
                  </th>
                  <th rowSpan={2} className="text-center align-middle">
                    Km
                  </th>
                  <th rowSpan={2} className="text-center align-middle">
                    Price
                  </th>
                  <th rowSpan={2} className="text-center align-middle">
                    Bank charge
                  </th>
                  <th rowSpan={2} className="text-center align-middle">
                    Payment type
                  </th>
                  <th rowSpan={2} className="text-center align-middle">
                    Amount
                  </th>
                  <th colSpan={5} className="text-center">
                    Driver Commission
                  </th>
                  <th colSpan={5} className="text-center">
                    Paid to Driver
                  </th>
                </tr>
                <tr>
                  {/* Driver Commission */}
                  <th className="text-center">Rate (%)</th>
                  <th className="text-center">Comm</th>
                  <th className="text-center">WIH-Tax 5%</th>
                  <th className="text-center">Net comm</th>
                  <th className="text-center">Request No</th>
                  {/* Paid to Driver */}
                  <th className="text-center">Date/RQ</th>
                  <th className="text-center">Date/Paid</th>
                  <th className="text-center">Amount</th>
                  <th className="text-center">Bank Charge</th>
                  <th className="text-center">Net Driver</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={21} className="text-center">
                      ไม่มีข้อมูล
                    </td>
                  </tr>
                ) : (
                  data.map((row, index) => (
                    <tr key={`${row.orderNo}-${index}`}>
                      <td className="text-nowrap">{formatDate(row.date)}</td>
                      <td className="text-right">{row.orderNo}</td>
                      <td>{row.driverName}</td>
                      <td className="text-right">{row.driverId}</td>
                      <td>{row.locationStart}</td>
                      <td>{row.locationEnding}</td>
                      <td className="text-right">{formatNumber(row.km)}</td>
                      <td className="text-right">{formatNumber(row.price)}</td>
                      <td className="text-right">
                        {formatNumber(row.bankCharge)}
                      </td>
                      <td>{row.paymentType}</td>
                      <td className="text-right">{formatNumber(row.amount)}</td>
                      {/* Driver Commission */}
                      <td className="text-right">{formatNumber(row.ratePct)}</td>
                      <td className="text-right">
                        {formatNumber(row.commission)}
                      </td>
                      <td className="text-right">{formatNumber(row.whtTax)}</td>
                      <td className="text-right">
                        {formatNumber(row.netCommission)}
                      </td>
                      <td className="text-right">
                        {row.requestNo ?? "-"}
                      </td>
                      {/* Paid to Driver */}
                      <td className="text-nowrap">{formatDate(row.dateRq)}</td>
                      <td className="text-nowrap">{formatDate(row.datePaid)}</td>
                      <td className="text-right">
                        {formatNumber(row.paidAmount)}
                      </td>
                      <td className="text-right">
                        {formatNumber(row.paidBankCharge)}
                      </td>
                      <td className="text-right">
                        {formatNumber(row.netDriver)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReportOverview;
